import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import CuttingOrderForm from "../../components/cuttingOrders/CuttingOrderForm";
import { pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearCuttingOrderPartyInfo,
  fetchCuttingOrderMaterials,
  fetchCuttingOrderParties,
  fetchCuttingOrderPartyInfo,
  getCuttingOrderMaterials,
  getCuttingOrderPartyInfo,
  getCuttingOrderPartyInfoLoading,
  getCuttingOrderPartyOptions,
} from "../../features/cuttingOrders/cuttingOrdersSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { POST } from "../../services/axios.service";
import { orderLinePayload } from "../../static/cuttingLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import {
  CuttingBarsPayload,
  CuttingSkipPieceProps,
  ICuttingOrderFormInput,
} from "../../types/cuttingOrder";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "કટીંગ ઓર્ડર", href: route.cuttingOrders },
  { label: "નવો ઓર્ડર" },
];

const CuttingOrderCreatePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const partyOptions = useAppSelector(getCuttingOrderPartyOptions);
  const materials = useAppSelector(getCuttingOrderMaterials);
  const partyInfo = useAppSelector(getCuttingOrderPartyInfo);
  const partyInfoLoading = useAppSelector(getCuttingOrderPartyInfoLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  const withRates = hasPermission(
    loggingUser?.permissions,
    permission.cuttingRate
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchCuttingOrderParties());
    dispatch(fetchCuttingOrderMaterials());
    dispatch(clearCuttingOrderPartyInfo());
  }, [dispatch]);

  const handlePartyChange = useCallback(
    (partyId: number) => {
      if (partyId) {
        dispatch(fetchCuttingOrderPartyInfo({ party_id: partyId }));
      } else {
        dispatch(clearCuttingOrderPartyInfo());
      }
    },
    [dispatch]
  );

  const handleSubmit = async (
    data: ICuttingOrderFormInput,
    sentBars: CuttingBarsPayload[],
    skipPieces: CuttingSkipPieceProps[]
  ) => {
    setIsSubmitting(true);
    try {
      await POST("/v1/admin/cutting-orders", {
        party_id: data.party_id,
        date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
        items: orderLinePayload(data.items, withRates),
        materials: sentBars,
        skip_pieces: skipPieces,
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.cuttingOrders);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>નવો કટીંગ ઓર્ડર ઉમેરો</h1>
      </div>

      <div className="flex flex-col gap-6">
        <CuttingOrderForm
          partyOptions={partyOptions}
          partyInfo={partyInfo}
          partyInfoLoading={partyInfoLoading}
          materials={materials}
          onPartyChange={handlePartyChange}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onCancel={() => navigate(route.cuttingOrders)}
          submitLabel="સાચવો"
          withRates={withRates}
        />
      </div>
    </AppLayout>
  );
};
export default CuttingOrderCreatePage;
